import { type ReactNode, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

interface ClampedTextProps {
  children: ReactNode;
  className: string;
}

/**
 * Clamps its text to 2 lines, with a "view more" link to reveal the rest when
 * the text is longer.
 */
export const ClampedText = ({ children, className }: ClampedTextProps) => {
  const textElementRef = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const textElement = textElementRef.current;
    if (!textElement) {
      return;
    }
    // Overflowing becomes true when the text height exceeds the ellipsis-2 height (40px)
    // 1px allowance avoids rounding glitches.
    const observer = new ResizeObserver(() =>
      setOverflowing(textElement.scrollHeight - textElement.clientHeight > 1),
    );
    observer.observe(textElement);
    return () => observer.disconnect();
  }, [children]);

  return (
    <div className={className}>
      <div
        ref={textElementRef}
        className={expanded ? undefined : 'ellipsis-2'}
      >
        {children}
      </div>
      {overflowing && !expanded && (
        <span
          className="text-color-primary cursor-pointer"
          onClick={() => setExpanded(true)}
        >
          <FormattedMessage id="common.viewMore" />
        </span>
      )}
    </div>
  );
};
